import { ASSET_CLASSES } from '../get-asset-class';
import { FUND_TYPES } from '../get-fund-type';
import format from '../../../utils/format';

const NOOP = () => {}; // eslint-disable-line no-empty-function

const getProductType = productType => {
  switch (productType) {
    case 'Non Qualified Annuity':
      return 'Personal Annuities';
    case 'RetirePlus':
      return 'RetirePlus Select';
    default:
      return productType;
  }
};

const FACTS = {
  productType: {
    title: 'Product Type',
    getValue: source => format.text(getProductType(source.fundType))
  },
  productFamily: {
    title: 'Product Family',
    getValue: source => format.text(source.fundType === 'TIAA QDIA Fixed Annuity'
     && source.fundFamily === '' ?
      'TIAA' : source.fundFamily)
  },
  contractType: {
    title: 'Contract Type',
    getValue: source => format.text(source.contractType)
  },
  inceptionDate: {
    title: 'Inception Date',
    getValue: source => format.date(source.fundType === 'TIAA Stable Value' ?
      source.tsvInceptionDate : source.inceptionDate)
  },
  annuityAccountInceptionDate: {
    title: 'Annuity Account Inception Date',
    getValue: source => format.date(source.inceptionDate)
  },
  portfolioInceptionDate: {
    title: 'Portfolio Inception Date',
    getValue: source => format.date(source.portfolioInceptionDate)
  },
  portfolioShareClass: {
    title: 'Portfolio Share Class',
    getValue: source => format.text(source.portfolioShareClass)
  },
  shareClass: {
    title: 'Share Class',
    getValue: source => format.text(source.shareClass)
  },
  productName: {
    title: 'Product Name',
    getValue: source => format.text(source.shareClass)
  },
  assetType: {
    title: 'Asset Type',
    getValue: source => format.text(source.assetType)
  },
  assetClass: {
    title: 'Asset Class',
    getValue: source => format.text(source.assetType)
  },
  underlyingFundName: {
    title: 'Underlying Fund Name',
    getValue: source => format.text(source.underlyingFundName)
  },
  underlyingFundInceptionDate: {
    title: 'Underlying Fund Inception Date',
    getValue: source => format.date(source.underlyingFundInception)
  },
  underlyingFundTicker: {
    title: 'Underlying Fund Ticker',
    getValue: source => format.text(source.underlyingFundTicker)
  },
  ticker: {
    title: 'Ticker',
    getValue: source => format.text(source.ticker)
  },
  cusip: {
    title: 'Cusip',
    getValue: source => format.text(source.cusip)
  },
  planNumber: {
    title: 'Plan Number',
    getValue: source => format.text(source.fundType === 'TIAA Stable Value' ?
      source.tsvPlanNumber : source.planNumber)
  },
  redemptionFee: {
    title: 'Redemption Fee',
    getValue: source => format.text(source.redemptionFee)
  },
  numberOfHoldings: {
    title: 'Number of Holdings',
    getValue: source =>
      format.number(source.numberOfHoldings, { formatString: '0,0' }),
    getAsOfDate: source => format.date(source.holdingsAsOfDate)
  },
  portfolioNetAssets: {
    title: 'Portfolio Net Assets',
    getValue: source =>
      format.number(source.netAssets, { formatString: '$0.00a' }),
    getAsOfDate: source => format.date(source.netAssetsAsOfDate)
  },
  returnOnEquity: {
    title: 'Return on Equity',
    getValue: source => format.percent(source.returnOnEquity),
    getAsOfDate: source => format.date(source.morningstarAsOfDate)
  },
  averageMaturity: {
    title: 'Average Maturity',
    getValue: source => format.number(source.averageMaturity),
    getAsOfDate: source => format.date(source.durationAndMaturityAsOfDate)
  },
  distributionFrequency: {
    title: 'Distribution Frequency',
    getValue: source => format.text(source.dividendFrequency)
  },
  optionAdjustedDuration: {
    title: 'Option Adjusted Duration',
    getValue: source => format.number(source.openAdjustedDuration),
    getAsOfDate: source => format.date(source.durationAndMaturityAsOfDate)
  },
  turnoverRatio: {
    title: 'Turnover Ratio',
    getValue: source => format.percent(source.turnOverRatio),
    getAsOfDate: source => format.date(source.turnOverRatioDate)
  },
  accountType: {
    title: 'Account Type',
    getValue: source => format.text(source.accountType)
  },
  subAccountInceptionDate: {
    title: 'Sub Account Inception Date',
    getValue: source => format.date(source.subAccountInceptionDate)
  }
};

const getFactConfigs = ({ fundType, assetClass }) => {
  //Cref Variable Annuities
  if (
    fundType === FUND_TYPES.variableAnnuityCref &&
    assetClass === ASSET_CLASSES.multiAsset
  ) {
    return [
      FACTS.productType,
      FACTS.productFamily,
      FACTS.inceptionDate,
      FACTS.portfolioInceptionDate,
      FACTS.shareClass,
      FACTS.assetType,
      FACTS.ticker,
      FACTS.cusip,
      FACTS.averageMaturity,
      FACTS.numberOfHoldings,
      FACTS.optionAdjustedDuration,
      FACTS.portfolioNetAssets,
      FACTS.returnOnEquity,
      FACTS.turnoverRatio
    ];
  }

  if (
    fundType === FUND_TYPES.variableAnnuityCref &&
    assetClass === ASSET_CLASSES.equities
  ) {
    return [
      FACTS.productType,
      FACTS.productFamily,
      FACTS.inceptionDate,
      FACTS.portfolioInceptionDate,
      FACTS.shareClass,
      FACTS.assetType,
      FACTS.ticker,
      FACTS.cusip,
      FACTS.numberOfHoldings,
      FACTS.portfolioNetAssets,
      FACTS.returnOnEquity,
      FACTS.turnoverRatio
    ];
  }

  if (
    fundType === FUND_TYPES.variableAnnuityCref &&
    assetClass === ASSET_CLASSES.fixedIncome
  ) {
    return [
      FACTS.productType,
      FACTS.productFamily,
      FACTS.inceptionDate,
      FACTS.portfolioInceptionDate,
      FACTS.shareClass,
      FACTS.assetType,
      FACTS.ticker,
      FACTS.cusip,
      FACTS.averageMaturity,
      FACTS.numberOfHoldings,
      FACTS.optionAdjustedDuration,
      FACTS.portfolioNetAssets,
      FACTS.turnoverRatio
    ];
  }

  if (
    fundType === FUND_TYPES.variableAnnuityCref &&
    assetClass === ASSET_CLASSES.moneyMarket
  ) {
    return [
      FACTS.productType,
      FACTS.productFamily,
      FACTS.inceptionDate,
      FACTS.portfolioInceptionDate,
      FACTS.shareClass,
      FACTS.assetType,
      FACTS.ticker,
      FACTS.cusip,
      FACTS.numberOfHoldings,
      FACTS.portfolioNetAssets,
      FACTS.turnoverRatio
    ];
  }
  //TIAA Access Variable Annuities
  if (
    fundType === FUND_TYPES.variableAnnuityTiaaAccess &&
    assetClass === ASSET_CLASSES.fixedIncome
  ) {
    return [
      FACTS.productType,
      FACTS.productFamily,
      FACTS.annuityAccountInceptionDate,
      FACTS.underlyingFundInceptionDate,
      FACTS.underlyingFundName,
      FACTS.underlyingFundTicker,
      FACTS.shareClass,
      FACTS.assetType,
      FACTS.cusip,
      FACTS.averageMaturity,
      FACTS.numberOfHoldings,
      FACTS.optionAdjustedDuration,
      FACTS.portfolioNetAssets,
      FACTS.turnoverRatio
    ];
  }

  if (
    fundType === FUND_TYPES.variableAnnuityTiaaAccess &&
    assetClass === ASSET_CLASSES.equities
  ) {
    return [
      FACTS.productType,
      FACTS.productFamily,
      FACTS.annuityAccountInceptionDate,
      FACTS.underlyingFundInceptionDate,
      FACTS.underlyingFundName,
      FACTS.underlyingFundTicker,
      FACTS.shareClass,
      FACTS.assetType,
      FACTS.cusip,
      FACTS.numberOfHoldings,
      FACTS.portfolioNetAssets,
      FACTS.returnOnEquity,
      FACTS.turnoverRatio
    ];
  }

  if (
    fundType === FUND_TYPES.variableAnnuityTiaaAccess &&
    (assetClass === ASSET_CLASSES.multiAsset ||
      assetClass === ASSET_CLASSES.moneyMarket)
  ) {
    return [
      FACTS.productType,
      FACTS.productFamily,
      FACTS.annuityAccountInceptionDate,
      FACTS.underlyingFundInceptionDate,
      FACTS.underlyingFundName,
      FACTS.underlyingFundTicker,
      FACTS.shareClass,
      FACTS.assetType,
      FACTS.cusip,
      FACTS.numberOfHoldings,
      FACTS.portfolioNetAssets,
      FACTS.turnoverRatio
    ];
  }
  //Mutual Funds
  if (
    fundType === FUND_TYPES.mutualFund &&
    assetClass !== ASSET_CLASSES.fixedIncome &&
    assetClass !== ASSET_CLASSES.equities
  ) {
    return [
      FACTS.productType,
      FACTS.productFamily,
      FACTS.inceptionDate,
      FACTS.portfolioInceptionDate,
      FACTS.shareClass,
      FACTS.assetType,
      FACTS.ticker,
      FACTS.cusip,
      FACTS.distributionFrequency,
      FACTS.numberOfHoldings,
      FACTS.portfolioNetAssets,
      FACTS.turnoverRatio
    ];
  }

  if (
    fundType === FUND_TYPES.mutualFund &&
    assetClass === ASSET_CLASSES.fixedIncome
  ) {
    return [
      FACTS.productType,
      FACTS.productFamily,
      FACTS.inceptionDate,
      FACTS.portfolioInceptionDate,
      FACTS.shareClass,
      FACTS.assetType,
      FACTS.ticker,
      FACTS.cusip,
      FACTS.distributionFrequency,
      FACTS.averageMaturity,
      FACTS.numberOfHoldings,
      FACTS.optionAdjustedDuration,
      FACTS.portfolioNetAssets,
      FACTS.turnoverRatio
    ];
  }

  if (
    fundType === FUND_TYPES.mutualFund &&
    assetClass === ASSET_CLASSES.equities
  ) {
    return [
      FACTS.productType,
      FACTS.productFamily,
      FACTS.inceptionDate,
      FACTS.portfolioInceptionDate,
      FACTS.shareClass,
      FACTS.assetType,
      FACTS.ticker,
      FACTS.cusip,
      FACTS.distributionFrequency,
      FACTS.numberOfHoldings,
      FACTS.portfolioNetAssets,
      FACTS.turnoverRatio,
      FACTS.returnOnEquity
    ];
  }
  //TIAA Real Estate
  if (fundType === FUND_TYPES.variableAnnuityTiaa) {
    return [
      FACTS.productType,
      FACTS.productFamily,
      FACTS.inceptionDate,
      FACTS.assetType,
      FACTS.ticker,
      FACTS.cusip,
      FACTS.numberOfHoldings,
      FACTS.portfolioNetAssets
    ];
  }
  // TIAA Traditional
  if (fundType === FUND_TYPES.fixedAnnuityTiaa) {
    return [
      FACTS.productType,
      FACTS.productFamily,
      FACTS.inceptionDate,
      FACTS.assetType,
      FACTS.cusip,
      FACTS.contractType
    ];
  }
  //TIAA Stable Value
  if (fundType === FUND_TYPES.fixedAnnuityTiaaStableValue) {
    return [
      FACTS.productType,
      FACTS.inceptionDate,
      FACTS.planNumber,
      FACTS.assetClass
    ];
  }

  //TIAA RetirePlus
  if (
    fundType === FUND_TYPES.retirePlus ||
    fundType === FUND_TYPES.retirePlusPro ||
    fundType === FUND_TYPES.customPortfolioModel
  ) {
    return [
      FACTS.productType,
      FACTS.inceptionDate,
      FACTS.accountType,
      FACTS.assetClass
    ];
  }

  //After Tax Annuity
  if (fundType === FUND_TYPES.afterTaxAnnuity && assetClass !== ASSET_CLASSES.moneyMarket) {
    return [
      FACTS.productType,
      FACTS.productName,
      FACTS.underlyingFundInceptionDate,
      FACTS.subAccountInceptionDate,
      FACTS.assetClass,
      FACTS.portfolioNetAssets
    ];
  }

  //After Tax Annuity Money Market
  if (fundType === FUND_TYPES.afterTaxAnnuity && assetClass === ASSET_CLASSES.moneyMarket) {
    return [
      FACTS.productType,
      FACTS.productName,
      FACTS.underlyingFundInceptionDate,
      FACTS.subAccountInceptionDate,
      FACTS.assetClass
    ];
  }

  //Secure Income Account
  if (fundType === FUND_TYPES.fixedAnnuityTiaaQdia) {
    return [
      FACTS.productFamily,
      FACTS.inceptionDate,
      FACTS.assetType,
      FACTS.cusip
    ];
  }
  //CITs and other
  if (fundType === FUND_TYPES.fixedAnnuity
    || fundType === FUND_TYPES.variableAnnuity
    || fundType === FUND_TYPES.otherInvestment) {
    return [
      FACTS.productType,
      FACTS.productFamily,
      FACTS.inceptionDate,
      FACTS.portfolioInceptionDate,
      FACTS.shareClass,
      FACTS.assetType,
      FACTS.ticker,
      FACTS.cusip,
      FACTS.distributionFrequency,
      FACTS.numberOfHoldings,
      FACTS.portfolioNetAssets,
      FACTS.turnoverRatio,
      FACTS.returnOnEquity
    ];
  }

  return [];
};

const mapInvestmentFacts = ({ source, configs }) =>
  configs.map(config => {
    const factObject = {};
    if (config) {
      const { title, getValue = NOOP, getAsOfDate = NOOP } = config;
      factObject.title = title;
      factObject.value = getValue(source);
      if (getAsOfDate(source)) {
        factObject.asOfDate = getAsOfDate(source);
      }
    }
    return factObject;
  });

const transformInvestmentFacts = (source, fundType, assetClass) => {
  if (!source.xid) {
    return [];
  }
  const configs = getFactConfigs({ fundType, assetClass });
  return mapInvestmentFacts({ source, configs });
};

export default transformInvestmentFacts;
